import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api, ApiError, downloadFile } from '../shared/api';
import { formatDate, formatMoney } from '../shared/formatting';
import { carrierLabel } from '../shared/orderStatus';

type Locale = 'hr' | 'en';

type TransferRow = {
    old_order_number: string;
    old_branch: string | null;
    old_order_date: string | null;
    new_order_number: string;
    new_branch: string | null;
    new_order_date: string | null;
    amount_eur: number | null;
    faros_status: string | null;
    status: string;
    status_label: string;
    status_date: string | null;
    carrier: string | null;
    napomena: string | null;
};

type TransferResp = {
    rows: TransferRow[];
    summary: Record<string, number>;
    branches: string[];
    faros_statuses: string[];
};

const STATUS_OPTIONS: Array<{ key: string; hr: string; en: string }> = [
    { key: 'bez_statusa', hr: 'Bez statusa', en: 'No status' },
    { key: 'u_dolasku', hr: 'U obradi', en: 'Processing' },
    { key: 'pakirano', hr: 'Pakirano', en: 'Packed' },
    { key: 'na_putu', hr: 'Poslano', en: 'Sent' },
    { key: 'poslano', hr: 'Poslano klijentu', en: 'Sent to customer' },
    { key: 'imam_ju', hr: 'Smješteno', en: 'Placed' },
    { key: 'isporuceno', hr: 'Isporučeno (ukinuto)', en: 'Delivered (retired)' },
];

/**
 * Prebacene narudzbe (Faros izvjestaj AD_NK_KOP, mail UID 988): nova narudzba uz status iz rucne alokacije
 * i datum tog statusa. Izvjestaj se podize kroz uvoz posti, ovdje se samo pregledava.
 */
export function OrderTransfersPanel({ locale, token }: { locale: Locale; token: string | null }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);

    const [open, setOpen] = useState(false);
    const [data, setData] = useState<TransferResp | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [branch, setBranch] = useState('');
    const [status, setStatus] = useState('');
    const [farosStatus, setFarosStatus] = useState('');
    const [q, setQ] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');

    const query = useMemo(() => {
        const p = new URLSearchParams();
        if (branch) p.set('branch', branch);
        if (status) p.set('status', status);
        if (farosStatus) p.set('faros_status', farosStatus);
        if (q.trim()) p.set('q', q.trim());
        if (dateFrom) p.set('date_from', dateFrom);
        if (dateTo) p.set('date_to', dateTo);
        return p.toString();
    }, [branch, status, farosStatus, q, dateFrom, dateTo]);

    const load = useCallback(() => {
        setLoading(true);
        setError(null);
        api<TransferResp>(`/order-transfers${query ? `?${query}` : ''}`, token)
            .then(setData)
            .catch((e) => setError(e instanceof ApiError ? e.message : t('Učitavanje nije uspjelo.', 'Loading failed.')))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, query, locale]);

    useEffect(() => {
        if (open) load();
    }, [open, load]);

    const exportXlsx = async () => {
        try {
            await downloadFile(`/order-transfers/export.xlsx${query ? `?${query}` : ''}`, token, `prebacene-narudzbe-${new Date().toISOString().slice(0, 10)}.xlsx`);
        } catch {
            setError(t('Izvoz nije uspio.', 'Export failed.'));
        }
    };

    const statusText = (r: TransferRow) => {
        const opt = STATUS_OPTIONS.find((o) => o.key === r.status);
        const label = opt ? t(opt.hr, opt.en) : r.status_label;
        const carrier = r.carrier ? ` (${carrierLabel(r.carrier, locale)})` : '';
        const date = r.status_date ? ` ${formatDate(r.status_date, locale)}` : '';
        return `${label}${carrier}${date}`;
    };

    return (
        <section style={{ marginTop: '1.6rem' }}>
            <button type="button" className="secondary compact" onClick={() => setOpen((v) => !v)}>
                {open ? '▾' : '▸'} {t('Prebačene narudžbe (Faros) i status u ručnoj alokaciji', 'Transferred orders (Faros) and manual allocation status')}
            </button>
            {open ? (
                <div style={{ marginTop: '0.6rem' }}>
                    <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', alignItems: 'end' }}>
                        <label>
                            {t('Nova poslovnica', 'New branch')}
                            <select value={branch} onChange={(e) => setBranch(e.target.value)}>
                                <option value="">{t('Sve', 'All')}</option>
                                {(data?.branches ?? []).map((b) => (
                                    <option key={b} value={b}>{b}</option>
                                ))}
                            </select>
                        </label>
                        <label>
                            {t('Status u BIS-u', 'BIS status')}
                            <select value={status} onChange={(e) => setStatus(e.target.value)}>
                                <option value="">{t('Svi', 'All')}</option>
                                {STATUS_OPTIONS.map((o) => (
                                    <option key={o.key} value={o.key}>{t(o.hr, o.en)}</option>
                                ))}
                            </select>
                        </label>
                        <label>
                            {t('Status u Farosu', 'Faros status')}
                            <select value={farosStatus} onChange={(e) => setFarosStatus(e.target.value)}>
                                <option value="">{t('Svi', 'All')}</option>
                                {(data?.faros_statuses ?? []).map((s) => (
                                    <option key={s} value={s}>{s}</option>
                                ))}
                            </select>
                        </label>
                        <label>
                            {t('Od', 'From')}
                            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
                        </label>
                        <label>
                            {t('Do', 'To')}
                            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
                        </label>
                        <label>
                            {t('Broj narudžbe', 'Order number')}
                            <input type="search" value={q} onChange={(e) => setQ(e.target.value)} />
                        </label>
                        <button type="button" className="secondary compact" onClick={exportXlsx} disabled={!data || data.rows.length === 0}>
                            {t('Izvoz u Excel', 'Export to Excel')}
                        </button>
                    </div>

                    {error ? <p className="error">{error}</p> : null}
                    {loading ? <p className="muted">{t('Učitavanje...', 'Loading...')}</p> : null}

                    {data ? (
                        <>
                            <p className="muted" style={{ fontSize: '0.85rem' }}>
                                {STATUS_OPTIONS.filter((o) => (data.summary[o.key] ?? 0) > 0)
                                    .map((o) => `${t(o.hr, o.en)} ${data.summary[o.key]}`)
                                    .join(', ') || t('Nema prebačenih narudžbi. Izvještaj se dodaje slanjem na adresu za obradu pošte.', 'No transferred orders yet.')}
                            </p>
                            {data.rows.length > 0 ? (
                                <div style={{ overflowX: 'auto' }}>
                                    <table className="data-table">
                                        <thead>
                                            <tr>
                                                <th>{t('Nova narudžba', 'New order')}</th>
                                                <th>{t('Datum', 'Date')}</th>
                                                <th>{t('Nova poslovnica', 'New branch')}</th>
                                                <th>{t('Prvotna narudžba', 'Original order')}</th>
                                                <th>{t('Iznos', 'Amount')}</th>
                                                <th>Faros</th>
                                                <th>{t('Status u BIS-u', 'BIS status')}</th>
                                                <th>{t('Napomena', 'Note')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data.rows.map((r) => (
                                                <tr key={`${r.old_order_number}|${r.new_order_number}`}>
                                                    <td>{r.new_order_number}</td>
                                                    <td>{r.new_order_date ? formatDate(r.new_order_date, locale) : '-'}</td>
                                                    <td>{r.new_branch ?? '-'}</td>
                                                    <td>{r.old_order_number}</td>
                                                    <td style={{ textAlign: 'right' }}>{r.amount_eur === null ? '-' : formatMoney(r.amount_eur, locale)}</td>
                                                    <td>{r.faros_status ?? '-'}</td>
                                                    <td>{statusText(r)}</td>
                                                    <td>{r.napomena ?? ''}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : null}
                        </>
                    ) : null}
                </div>
            ) : null}
        </section>
    );
}
